import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Suspense, lazy } from "react";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import ErrorBoundary from "./components/ErrorBoundary";
import { PageLoader } from "./components/LoadingSpinner";
import { Layout } from "./components/Layout";

// Lazy load pages
const BlogListing = lazy(() => import("./pages/BlogListing"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const SchoolAudit = lazy(() => import("./pages/SchoolAudit"));
const CorporateAudit = lazy(() => import("./pages/CorporateAudit"));
const FreeMockup = lazy(() => import("./pages/FreeMockup"));
const AcademicCalendar = lazy(() => import("./pages/AcademicCalendar"));
const CorporateChecklist = lazy(() => import("./pages/CorporateChecklist"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const ColorChecker = lazy(() => import("./pages/ColorChecker"));

// Admin pages
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminLeads from "./pages/admin/AdminLeads";
import AdminSubscribers from "./pages/admin/AdminSubscribers";
import AdminContacts from "./pages/admin/AdminContacts";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Routes>
                <Route element={<Layout />}>
                  <Route path="/" element={<Index />} />
                  <Route path="/blog" element={<Suspense fallback={<PageLoader />}><BlogListing /></Suspense>} />
                  <Route path="/blog/:slug" element={<Suspense fallback={<PageLoader />}><BlogPost /></Suspense>} />
                  <Route path="/free-school-branding-audit" element={<Suspense fallback={<PageLoader />}><SchoolAudit /></Suspense>} />
                  <Route path="/free-corporate-branding-audit" element={<Suspense fallback={<PageLoader />}><CorporateAudit /></Suspense>} />
                  <Route path="/free-mockup" element={<Suspense fallback={<PageLoader />}><FreeMockup /></Suspense>} />
                  <Route path="/academic-branding-calendar" element={<Suspense fallback={<PageLoader />}><AcademicCalendar /></Suspense>} />
                  <Route path="/corporate-rebranding-checklist" element={<Suspense fallback={<PageLoader />}><CorporateChecklist /></Suspense>} />
                  <Route path="/portfolio" element={<Suspense fallback={<PageLoader />}><Portfolio /></Suspense>} />
                  <Route path="/tools/color-checker" element={<Suspense fallback={<PageLoader />}><ColorChecker /></Suspense>} />
                </Route>

                {/* Admin routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="leads" element={<AdminLeads />} />
                  <Route path="subscribers" element={<AdminSubscribers />} />
                  <Route path="contacts" element={<AdminContacts />} />
                </Route>

                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
